import React, { useEffect, useState } from "react"
import { Box, Grid, Typography, useTheme, IconButton } from "@mui/material"
import { Create } from "@mui/icons-material"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import { useDispatch, useSelector } from "react-redux"
import {
  getSectionTypesAction,
  getSectionTypeDetailAction,
  deleteSectionTypeAction,
  deleteSectionFieldAction,
  reorderSectionTypesAction,
  reorderSectionFieldsAction
} from "redux/superAdmin/actions/user"
import { superAdminAction } from "redux/superAdmin/slice/user"
import trashIcon from "assets/icons/trash.svg"
import styles from "./section.module.scss"
import SectionModal from "./SectionModal"
import FieldModal from "./FieldModal"

const SectionManagement = () => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [breadCrumbs, setBreadCrumbs] = useState([])
  const [openSectionModal, setOpenSectionModal] = useState(false)
  const [openFieldModal, setOpenFieldModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [modalData, setModalData] = useState(null)
  const [fieldModalData, setFieldModalData] = useState(null)
  const [isFieldEdit, setIsFieldEdit] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deleteData, setDeleteData] = useState(null)
  const [deleteType, setDeleteType] = useState(null)
  const [sectionList, setSectionList] = useState([])
  const [fieldList, setFieldList] = useState([])
  const { sectionTypes, selectedSectionType: selectedSection } = useSelector(
    (store) => store?.superAdmin
  )

  useEffect(() => {
    document.title = "Preshift Section Management"
    setBreadCrumbs(createBreadcrumbs())
    dispatch(superAdminAction.setSelectedSectionType(null))
    dispatch(getSectionTypesAction())
  }, [])

  useEffect(() => {
    if (sectionTypes?.length > 0 && !selectedSection) {
      dispatch(getSectionTypeDetailAction({ id: sectionTypes[0].id }))
    }
  }, [sectionTypes, selectedSection])

  useEffect(() => {
    setSectionList(sectionTypes ?? [])
  }, [sectionTypes])

  useEffect(() => {
    setFieldList(selectedSection?.fields_list ?? [])
  }, [selectedSection])

  const handleSectionClick = (section) => {
    dispatch(getSectionTypeDetailAction({ id: section.id }))
  }

  const handleOpenSectionModal = (data = null, isEdit = false) => {
    setIsEdit(isEdit)
    setModalData(data)
    setOpenSectionModal(true)
  }

  const handleOpenFieldModal = (data = null, isEdit = false) => {
    setIsFieldEdit(isEdit)
    setFieldModalData(data)
    setOpenFieldModal(true)
  }

  const handleDeleteConfirm = (item, type) => {
    setDeleteData(item)
    setDeleteType(type)
    setOpenDeleteDialog(true)
  }

  const handleDelete = () => {
    if (deleteType === "section") {
      dispatch(
        deleteSectionTypeAction({
          id: deleteData.id,
          callback: () => {
            setOpenDeleteDialog(false)
            dispatch(superAdminAction.setSelectedSectionType(null))
          }
        })
      )
    } else {
      dispatch(
        deleteSectionFieldAction({
          id: deleteData.id,
          section_type_id: selectedSection.id,
          callback: () => {
            setOpenDeleteDialog(false)
          }
        })
      )
    }
  }

  const handleSectionDragEnd = (result) => {
    if (!result.destination || result.source.index === result.destination.index) return
    const items = Array.from(sectionList)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)
    setSectionList(items)
    dispatch(
      reorderSectionTypesAction({
        payload: { types: items.map((item, index) => ({ id: item.id, order: index })) }
      })
    )
  }

  const handleFieldDragEnd = (result) => {
    if (!result.destination || result.source.index === result.destination.index) return
    const items = Array.from(fieldList)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)
    setFieldList(items)
    dispatch(
      reorderSectionFieldsAction({
        section_type_id: selectedSection.id,
        payload: {
          section_type: selectedSection.id,
          fields: items.map((item, index) => ({ id: item.id, order: index }))
        }
      })
    )
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Header title="Preshift Section Management" breadCrumbs={breadCrumbs} />
      </Grid>

      <Grid item xs={12} md={4}>
        <Box className={styles.arrowMenu}>
          <Box className={styles.arrowMenuContent}>
            <Box className="d-flex justify-content-between align-items-center mb-4">
              <Typography variant="h3" color="white">
                Sections
              </Typography>
              <AppButton
                onClick={() => handleOpenSectionModal()}
                styles={{ padding: "8px 16px", fontSize: "12px" }}>
                Add Section
              </AppButton>
            </Box>

            <DragDropContext onDragEnd={handleSectionDragEnd}>
              <Droppable droppableId="section-types">
                {(provided) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}>
                    {sectionList?.map((section, index) => (
                      <Draggable key={section.id} draggableId={String(section.id)} index={index}>
                        {(draggableProvided, snapshot) => (
                          <Box
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            className={`${styles.category_grid} d-flex justify-content-between`}
                            sx={{
                              backgroundColor:
                                selectedSection?.id === section.id
                                  ? "rgba(173, 145, 111, 0.05)"
                                  : snapshot.isDragging
                                  ? "rgba(255,255,255,0.05)"
                                  : "transparent",
                              borderColor:
                                selectedSection?.id === section.id
                                  ? "rgba(173, 145, 111, 0.3)"
                                  : "rgba(255,255,255,0.1)"
                            }}
                            onClick={() => handleSectionClick(section)}>
                            <Box className="d-flex align-items-center" sx={{ gap: 1 }}>
                              <Box {...draggableProvided.dragHandleProps} sx={{ display: "flex" }}>
                                <DragIndicatorIcon
                                  fontSize="small"
                                  sx={{ color: "rgba(255,255,255,0.3)", cursor: "grab" }}
                                />
                              </Box>
                              <Typography variant="body1Regular" color="white">
                                {section.name}
                              </Typography>
                            </Box>

                            <Box>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleOpenSectionModal(section, true)
                                }}
                                sx={{ color: "white" }}>
                                <Create fontSize="small" htmlColor={theme.palette.white.main} />
                              </IconButton>
                              {section.section_key !== "SHIFT_REPORT" && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDeleteConfirm(section, "section")
                                  }}
                                  sx={{ color: "white" }}>
                                  <img src={trashIcon} alt="delete" height={18} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={8} sx={{ position: "relative" }}>
        <Box className={styles.arrow} />

        <Box
          sx={{
            background: theme.palette.primary.main,
            borderRadius: "12px",
            padding: "32px",
            marginLeft: "34px",
            minHeight: "80vh",
            border: `1px solid rgba(173, 145, 111, 0.2)`
          }}>
          {selectedSection ? (
            <>
              {(() => {
                const isEditable = ["CUSTOM", "NOTES", "TEXT", "COVERS", "VIP_GUESTS"].includes(
                  selectedSection?.section_key
                )
                const isNotes = selectedSection?.section_key === "NOTES"
                const isText = selectedSection?.section_key === "TEXT"
                const existingFieldTypes =
                  selectedSection?.fields_list?.map((f) => f.field_type) ?? []
                const notesIsFull =
                  isNotes &&
                  existingFieldTypes.includes("rich_text") &&
                  existingFieldTypes.includes("items")
                const textIsFull = isText && existingFieldTypes.includes("rich_text")
                const canAddField = isEditable && !notesIsFull && !textIsFull

                return (
                  <>
                    <Box className="d-flex justify-content-between align-items-center mb-4">
                      <Typography variant="h3" color={theme.palette.text.primary}>
                        Fields for {selectedSection.name}
                      </Typography>
                      {canAddField && (
                        <AppButton
                          onClick={() => handleOpenFieldModal()}
                          styles={{ padding: "8px 16px", fontSize: "12px" }}>
                          Add Field
                        </AppButton>
                      )}
                    </Box>

                    {!isEditable && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color={theme.palette.text.secondary}>
                          This section is system-generated and cannot be manually modified.
                        </Typography>
                      </Box>
                    )}

                    <DragDropContext onDragEnd={handleFieldDragEnd}>
                      <Droppable droppableId="section-fields" isDropDisabled={!isEditable}>
                        {(provided) => (
                          <Grid
                            container
                            spacing={2}
                            ref={provided.innerRef}
                            {...provided.droppableProps}>
                            {fieldList?.map((field, index) => (
                              <Draggable
                                key={field.id}
                                draggableId={String(field.id)}
                                index={index}
                                isDragDisabled={!isEditable}>
                                {(draggableProvided, snapshot) => (
                                  <Grid
                                    item
                                    xs={12}
                                    ref={draggableProvided.innerRef}
                                    {...draggableProvided.draggableProps}>
                                    <Box
                                      sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: snapshot.isDragging
                                          ? "rgba(255,255,255,0.08)"
                                          : "rgba(255,255,255,0.05)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center"
                                      }}>
                                      <Box className="d-flex align-items-center" sx={{ gap: 1 }}>
                                        {isEditable && fieldList?.length > 1 && (
                                          <Box
                                            {...draggableProvided.dragHandleProps}
                                            sx={{ display: "flex" }}>
                                            <DragIndicatorIcon
                                              fontSize="small"
                                              sx={{
                                                color: "rgba(255,255,255,0.3)",
                                                cursor: "grab"
                                              }}
                                            />
                                          </Box>
                                        )}
                                        <Typography variant="body1Bold" color="white">
                                          {field.field_label}
                                        </Typography>
                                      </Box>
                                      {isEditable && (
                                        <Box>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleOpenFieldModal(field, true)}>
                                            <Create
                                              fontSize="small"
                                              htmlColor={theme.palette.white.main}
                                            />
                                          </IconButton>
                                          {!isNotes && (
                                            <IconButton
                                              size="small"
                                              sx={{ color: "white" }}
                                              onClick={() => handleDeleteConfirm(field, "field")}>
                                              <img src={trashIcon} alt="delete" height={16} />
                                            </IconButton>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  </Grid>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Grid>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </>
                )
              })()}
            </>
          ) : (
            <Box className="d-flex justify-content-center align-items-center h-100">
              <Typography variant="h4" color={theme.palette.text.secondary}>
                Select a section to manage its configuration
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>

      <SectionModal
        open={openSectionModal}
        onClose={() => {
          setOpenSectionModal(false)
          setIsEdit(!isEdit)
        }}
        isEdit={isEdit}
        sectionData={modalData}
      />

      <FieldModal
        open={openFieldModal}
        onClose={() => {
          setOpenFieldModal(false)
          setIsFieldEdit(!isFieldEdit)
        }}
        isEdit={isFieldEdit}
        fieldData={fieldModalData}
        sectionTypeId={selectedSection?.id}
        sectionKey={selectedSection?.section_key}
        existingFieldTypes={selectedSection?.fields_list?.map((f) => f.field_type) ?? []}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onDelete={handleDelete}
        title={deleteType === "section" ? "Delete Section" : "Delete Field"}
        description={
          deleteType === "section"
            ? `Deleting this section will also remove its corresponding data from all existing pre-shifts. Are you sure you want to continue?`
            : `Are you sure you want to delete the "${deleteData?.field_label}" field? This action cannot be undone.`
        }
      />
    </Grid>
  )
}

export default SectionManagement
